import React, { useEffect, useState } from 'react';
import { useDynamicEntitiesContext, useDynamicEntitiesPermissions } from '../core/DynamicEntitiesProvider';
import { PencilSquareIcon, DocumentTextIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useFeatureService } from './services/useFeatureService';
import type { Feature } from './services/FeatureService';
import { Button } from '../../Button/Button';
import { Switch } from '../../Switch/Switch';

export interface FeatureListProps {
    onSelectFeature?: (id: string) => void;
    onCreatePlansAndCriteria?: (feature: Feature) => void;
    searchTerm?: string;
}

export const FeatureList: React.FC<FeatureListProps> = ({ onSelectFeature, onCreatePlansAndCriteria, searchTerm = '' }) => {
    const { onError } = useDynamicEntitiesContext();
    const { canUpdate } = useDynamicEntitiesPermissions();
    const service = useFeatureService();

    const [features, setFeatures] = useState<Feature[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<Error | null>(null);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [togglingId, setTogglingId] = useState<string | null>(null);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 300);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const loadFeatures = async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const data = await service.getAll({ page: 1, limit: 100, search: debouncedSearch });
            setFeatures(data.data);
        } catch (error) {
            // Un fallo de lectura tiene que verse: antes un 401 pintaba el mismo
            // vacío que "no hay maestros creados".
            console.error('Error loading features:', error);
            setFeatures([]);
            setLoadError(error as Error);
            onError?.(error as Error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadFeatures();
    }, [service, debouncedSearch]);

    const handleToggleFileManagement = async (feature: Feature) => {
        if (togglingId === feature.id) return;
        setTogglingId(feature.id);
        try {
            await service.setFileManagement(feature.id, !feature.hasFileManagement);
            setFeatures(prev =>
                prev.map(f => f.id === feature.id ? { ...f, hasFileManagement: !f.hasFileManagement } : f)
            );
        } catch (error) {
            console.error('Error actualizando gestión de archivos:', error);
            onError?.(error as Error);
            alert('Error al actualizar la gestión de archivos. Revisa la consola.');
        } finally {
            setTogglingId(null);
        }
    };

    return (
        <div className="bg-white rounded-2xl w-full p-5">
            {loading ? (
                <div className="p-6 text-gray-500">Cargando...</div>
            ) : loadError ? (
                <div
                    role="alert"
                    className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3"
                >
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                    <div className="flex flex-col gap-1">
                        <p className="text-sm font-medium text-red-700">
                            No se pudieron cargar los maestros.
                        </p>
                        <p className="text-sm text-red-600">{loadError.message}</p>
                    </div>
                </div>
            ) : (
                <div className="w-full overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr>
                                <th className="px-5 py-4 text-sm font-medium text-gray-400 border-b border-gray-200">Código</th>
                                <th className="px-5 py-4 text-sm font-medium text-gray-400 border-b border-gray-200">Nombre</th>
                                <th className="px-5 py-4 text-sm font-medium text-gray-400 border-b border-gray-200">Tipo</th>
                                <th className="px-5 py-4 text-sm font-medium text-gray-400 border-b border-gray-200 text-center">Gestión de Archivos</th>
                                <th className="px-5 py-4 text-sm font-medium text-gray-400 border-b border-gray-200 text-center w-[100px]">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {features.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-5 py-6 text-center text-gray-500">
                                        No hay maestros creados.
                                    </td>
                                </tr>
                            )}
                            {features.map((feature) => (
                                <tr key={feature.id} className="group">
                                    <td className="px-5 py-4 text-sm text-gray-900 border-b border-gray-200">
                                        {feature.code}
                                    </td>
                                    <td className="px-5 py-4 text-sm text-gray-900 border-b border-gray-200">
                                        {feature.description || feature.name}
                                    </td>
                                    <td className="px-5 py-4 text-sm border-b border-gray-200">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-normal bg-[#dbeefe] text-[#0e79fd]">
                                            {feature.type === 'Transaction' ? 'Transacción' :
                                                feature.type === 'Document' ? 'Documento' :
                                                    'Maestro'}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4 text-sm border-b border-gray-200">
                                        <div className="flex items-center justify-center w-full">
                                            <Switch
                                                checked={!!feature.hasFileManagement}
                                                onCheckedChange={() => handleToggleFileManagement(feature)}
                                                disabled={!canUpdate || togglingId === feature.id}
                                            />
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 text-sm border-b border-gray-200 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            <Button
                                                type="outline"
                                                size="xs"
                                                onClick={() => onSelectFeature && onSelectFeature(feature.id)}
                                                leftIcon={<PencilSquareIcon className="h-3 w-3" />}
                                                ariaLabel="Editar"
                                                title="Entidad Dinámica"
                                            >
                                                ED
                                            </Button>
                                            {feature.allowsPlansAndCriteria && onCreatePlansAndCriteria && (
                                                <Button
                                                    type="outline"
                                                    size="xs"
                                                    onClick={() => onCreatePlansAndCriteria(feature)}
                                                    leftIcon={<DocumentTextIcon className="h-3 w-3" />}
                                                    ariaLabel="Planes y Criterios"
                                                    title="Planes y Criterios"
                                                >
                                                    PC
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
